// src/app/login/page.tsx
"use client";

import { useEffect, useMemo, useState, Suspense } from "react";
import { signIn, getProviders } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, EyeOff } from "lucide-react";
import InlineBanner from "@/components/ui/inline-banner";
import { API_BASE } from "@/lib/media";
import { getBrowserBase } from "@/lib/apiBase";
import { clearApiToken } from "@/lib/token";
import { collectRegistrationMetadata, writeRegMetaCookie } from "@/lib/tracking";
import Link from "next/link";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

const isSafeInternal = (url: string) => {
  try {
    // Only check on client-side to avoid hydration mismatch
    if (typeof window === 'undefined') return true;
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin;
  } catch { return false; }
};

// Rewrite any NextAuth absolute URL (e.g., http://localhost:3000/api/auth/callback/...)
// to the current origin so cookies are set on the correct host/port.
function navigateSameOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const u = new URL(url, window.location.origin);
    // Always force navigation to same-origin path + query only
    const dest = u.pathname + (u.search || "");
    window.location.href = dest;
    return true;
  } catch {
    return false;
  }
}

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();

  // Support both returnTo (preferred) and callbackUrl (compat) and validate same-origin
  const rawReturn = params.get("returnTo");
  const rawCallback = params.get("callbackUrl");
  const rawTarget = rawReturn || rawCallback || "/";
  const hadExplicit = params.has("returnTo") || params.has("callbackUrl");
  const callbackUrl = useMemo(() => {
    // Only process URLs on client-side to avoid hydration mismatch
    if (typeof window === 'undefined') return "/";
    if (!isSafeInternal(rawTarget)) return "/";
    const u = new URL(rawTarget, window.location.origin);
    // Never honor a returnTo that points back at an auth page. "Return to the
    // login page after logging in" is nonsensical, and it is exactly the shape
    // that let a stray /login?returnTo=/login redirect re-wrap itself into an
    // unbounded loop (2026-07-29 incident). Collapse any such target to "/".
    if (/^\/(login|register|verify|forgot|reset|onboarding)(\/|$)/i.test(u.pathname)) {
      return "/";
    }
    const path = u.pathname + (u.search || "");
    return path || "/";
  }, [rawTarget]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [networkIssue, setNetworkIssue] = useState<string | null>(null);
  const [acceptTos, setAcceptTos] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle redirects that already landed on ?error=...
  useEffect(() => {
    const qerr = params.get("error");
    if (!qerr) return;
    if (/configuration/i.test(qerr)) setError("Auth configuration error. Ensure NEXTAUTH_SECRET and NEXTAUTH_URL are set.");
    else if (/accessdenied|credentialssignin/i.test(qerr)) setError("Invalid email or password.");
    else if (/twitter_email_required/i.test(qerr)) setError("Your Twitter account must have a verified email address. Please add and verify an email at twitter.com/settings/your_twitter_data/account, then try again.");
    else setError("Login failed. Please try again.");
  }, [params]);

  const googleEnabledEnv = typeof process !== 'undefined' && (process.env.NEXT_PUBLIC_GOOGLE_ENABLED === '1');
  const [providers, setProviders] = useState<Record<string, any> | null>(null);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const prov = await getProviders();
        if (active) setProviders(prov as any);
      } catch {}
      finally { if (active) setProvidersLoaded(true); }
    })();
    return () => { active = false; };
  }, []);
  const googleAvailable = !!(providers && (providers as any).google);
  const twitterAvailable = !!(providers && (providers as any).twitter);
  // Only show OAuth providers after mount to prevent hydration mismatch
  const showGoogle = mounted && (googleAvailable || googleEnabledEnv);
  const showTwitter = mounted && twitterAvailable;

  async function onGoogleClick() {
    setError(null);
    if (!acceptTos) {
      setError('Please accept the Terms to continue.');
      return;
    }
    try {
      // Clear any existing auth state before signing in
      clearApiToken();
      // Cookie carries tracking metadata across OAuth roundtrip; ignored if user already exists.
      writeRegMetaCookie(collectRegistrationMetadata('google'));
      // Direct redirect (NextAuth default). The prior redirect:false + manual
      // window.location.href split the launch across an async gap, which is
      // unreliable on iOS Safari (lost user gesture / OAuth state-cookie timing),
      // leaving the user stuck on the form with password autofill re-prompting.
      await signIn('google', { callbackUrl });
    } catch (e) {
      setError("Google login is not configured on this server.");
    }
  }

  async function onTwitterClick() {
    setError(null);
    if (!acceptTos) {
      setError('Please accept the Terms to continue.');
      return;
    }
    try {
      // Clear any existing auth state before signing in
      clearApiToken();
      writeRegMetaCookie(collectRegistrationMetadata('twitter'));
      // Direct redirect (see Google handler note): reliable on iOS Safari.
      await signIn('twitter', { callbackUrl });
    } catch (e) {
      setError("Twitter login is not configured on this server.");
    }
  }

  const validate = () =>
    !email.trim() ? "Please enter your email." :
    !password.trim() ? "Please enter your password." : null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setNetworkIssue(null);
    const v = validate();
    if (v) return setError(v);
    if (!acceptTos) { setError('Please accept the Terms to continue.'); return; }

    setSubmitting(true);
    try {
      // Clear any existing auth state before signing in (prevents stale token issues)
      clearApiToken();

      const res = await signIn("credentials", {
        redirect: false,
        email,
        password,
        callbackUrl,
      });

      // Dev-only: detect "email not confirmed" from API without leaking in prod
      if (res && res.error && process.env.NODE_ENV === "development") {
        try {
          const probe = await fetch(`${API_BASE}/api/auth/token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
          });
          if (probe.status === 403) {
            setError("Please verify your email before signing in.");
            return;
          }
        } catch { /* ignore; fall through */ }
      }

      // NextAuth error URL surfaced even with redirect:false
      if (res && typeof res.url === "string" && /\/api\/auth\/error\b/.test(res.url)) {
        try {
          const u = new URL(res.url, window.location.origin);
          const code = u.searchParams.get("error") || "Error";
          if (/configuration/i.test(code)) setError("Auth configuration error. Ensure NEXTAUTH_SECRET and NEXTAUTH_URL are set.");
          else if (/accessdenied|credentialssignin/i.test(code)) setError("Invalid email or password.");
          else setError("Login failed. Please try again.");
        } catch { setError("Login failed. Please try again."); }
        return;
      }

      // Fallback: creds vs API unreachable
      if (!res || res.error) {
        try {
          const hc = await fetch(`${API_BASE}/api/health`, { cache: "no-store" });
          if (!hc.ok) throw new Error(String(hc.status));
          setError("Invalid email or password.");
        } catch {
          setNetworkIssue(`Can't reach API at ${API_BASE || "(empty)"}. Check that it's running and the dev certificate is trusted.`);
        }
        setSubmitting(false);
        return;
      }

      // SUCCESS: Hard navigation to force full page reload with fresh session
      const destination = (res.url && !/\/api\/auth\/error\b/.test(res.url))
        ? new URL(res.url, window.location.origin).pathname + (new URL(res.url, window.location.origin).search || '')
        : callbackUrl;

      // Use window.location.href for FULL page reload - not router.push
      // This ensures SessionProvider remounts and fetches fresh session
      // No cache buster needed - next.config.ts headers prevent caching
      window.location.href = destination;
      return;
    } catch {
      setError("Unexpected error while signing in. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4">
      <Card className="w-full max-w-md rounded-2xl border border-lime-400 dark:border-zinc-700 auth-panel text-zinc-950 dark:text-zinc-100">
        <CardHeader className="pb-2 text-center">
          <CardTitle className="text-[28px] sm:text-3xl text-zinc-950 dark:text-zinc-100">
            Sign In to JuicyStarz
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={onSubmit} className="space-y-5" aria-busy={submitting ? 'true' : 'false'}>
            {networkIssue && (
              <InlineBanner variant="error" className="text-center">
                <div className="space-x-2">
                  <span>{networkIssue}</span>
                  {mounted && process.env.NODE_ENV === "development" && (
                    <button
                      type="button"
                      className="underline text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                      onClick={(ev) => { ev.preventDefault(); try { window.open(`${API_BASE}/api/health`, "_blank"); } catch {} }}
                    >
                      Open health check
                    </button>
                  )}
                  {mounted && process.env.NODE_ENV === "development" && (
                    <button
                      type="button"
                      className="underline text-xs ml-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                      onClick={(ev) => { ev.preventDefault(); try { window.open('/api/auth/providers', '_blank'); } catch {} }}
                    >
                      Open providers
                    </button>
                  )}
                </div>
              </InlineBanner>
            )}

            {error && (
              <>
                <InlineBanner variant="error" className="text-center">{error}</InlineBanner>
                {/^please verify your email/i.test(error) && (
                  <div className="text-center text-xs mt-2">
                    <a href={`/verify/resend?email=${encodeURIComponent(email)}`} className="underline">Resend verification email</a>
                  </div>
                )}
              </>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
                className="mt-1 h-11 bg-white dark:bg-zinc-800 border-lime-400 dark:border-lime-500"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                  className="mt-1 h-11 pr-10 bg-white dark:bg-zinc-800 border-lime-400 dark:border-lime-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-pressed={showPw}
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
          </div>

            {/* Terms acceptance */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Terms of Service</legend>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={acceptTos}
                    onChange={(e) => setAcceptTos(e.currentTarget.checked)}
                    disabled={submitting}
                  />
                  <span>I agree to the <a className="underline" href="/terms" target="_blank" rel="noopener">Terms</a> and <a className="underline" href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>.</span>
                </label>
              </div>
            </fieldset>

            <Button type="submit" disabled={submitting} variant="primary" className="w-full h-11 rounded-lg">
              {submitting
                ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Signing in…</span>
                : "Log In"}
            </Button>
          </form>

          {/* Social sign-in below email form */}
          {(showGoogle || showTwitter) && (
            <div className="mt-5">
              <div className="mb-4 flex items-center gap-3 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <span>or continue with</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="space-y-2">
                {showGoogle && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full h-10 rounded-lg inline-flex items-center justify-center gap-2 bg-white text-gray-900 border hover:bg-white/90 dark:bg-zinc-100 dark:text-zinc-900"
                    onClick={onGoogleClick}
                    aria-label="Continue with Google"
                  >
                    {/* Inline Google G icon */}
                    <svg aria-hidden="true" focusable="false" className="h-4 w-4" viewBox="0 0 533.5 544.3"><path fill="#4285F4" d="M533.5 278.4c0-17.4-1.6-34.1-4.7-50.3H272.1v95.2h147.2c-6.4 34.6-25.9 63.9-55.2 83.5v69.2h89.3c52.2-48.2 80.1-119.2 80.1-197.6z"/><path fill="#34A853" d="M272.1 544.3c73.6 0 135.4-24.3 180.5-65.9l-89.3-69.2c-24.8 16.7-56.6 26.5-91.2 26.5-70 0-129.4-47.2-150.7-110.6H28.5v69.9C73.3 486 166.6 544.3 272.1 544.3z"/><path fill="#FBBC05" d="M121.4 325.1c-10.4-30.9-10.4-64.4 0-95.3V160H28.5c-38.4 76.8-38.4 167.6 0 244.4l92.9-69.3z"/><path fill="#EA4335" d="M272.1 106.1c38.8-.6 76 13.7 104.2 40.2l77.8-77.8C404.8 24.1 342.9-.1 272.1 0 166.6 0 73.3 58.3 28.5 160l92.9 69.9C144.7 166 203.9 106.8 272.1 106.1z"/></svg>
                    <span>Continue with Google</span>
                  </Button>
                )}
                {showTwitter && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full h-10 rounded-lg inline-flex items-center justify-center gap-2 bg-black text-white border hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90"
                    onClick={onTwitterClick}
                    aria-label="Continue with Twitter"
                  >
                    {/* Twitter/X icon */}
                    <svg aria-hidden="true" focusable="false" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    <span>Continue with Twitter</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="py-4 px-6">
          <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:justify-between">
            <Button asChild variant="secondary" size="sm" className="h-9 px-3 w-full sm:w-auto">
              <Link href="/forgot">Forgot password</Link>
            </Button>
            <Button asChild variant="secondary" size="sm" className="h-9 px-3 w-full sm:w-auto">
              <Link href={`/register?returnTo=${encodeURIComponent(callbackUrl)}`}>Create account</Link>
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-[calc(100vh-140px)] flex items-center justify-center">
        <div className="text-center">Loading...</div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
